/**
 * TNCY-5 (XT-D26) — pending USER-invitations table, relocated onto the
 * Users page.
 *
 * Background: the standalone Administration → Invitations page collided
 * by name with the new Partner Companies page (cross-tenant company
 * links). Per Christian 2026-06-23 the pending USER-invites table moves
 * here so all user management lives together. This is a **pure
 * relocation** — the data, hooks (`useInvitationsQuery` /
 * `useResend…` / `useRevoke…`) and per-row Resend / Revoke behaviour are
 * unchanged from the retired `InvitationsPage`.
 *
 * Rendered as a titled section beneath the user list on
 * `UsersListPage`. The "Invite user" affordance stays on the Users page
 * header (`InviteUserDialog`).
 */
import { useState, type ReactElement } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  useInvitationsQuery,
  useResendInvitationMutation,
  useRevokeInvitationMutation,
  type Invitation,
} from "../queries/index.js";
import { roleLabel, relativeTime } from "../shared/formatters.js";
import { RoleChip } from "../shared/RoleChip.js";
import { ConfirmDialog } from "../shared/ConfirmDialog.js";
import { useToast } from "../../account/ToastContext.js";

export function PendingInvitationsSection(): ReactElement {
  const { data, isPending, isError } = useInvitationsQuery({});

  return (
    <Box component="section" aria-label="Pending invitations" sx={{ mt: 5 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Pending invitations
      </Typography>
      {isPending ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error">Couldn&apos;t load invitations.</Typography>
      ) : data.items.length > 0 ? (
        <InvitationsGrid items={data.items} />
      ) : (
        <Box sx={{ py: 3, color: "text.secondary" }}>
          <Typography variant="body2">
            No pending invitations. Invite someone with the button above.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

interface InvitationsGridProps {
  items: Invitation[];
}

function InvitationsGrid({ items }: InvitationsGridProps): ReactElement {
  const { toast } = useToast();
  const resend = useResendInvitationMutation();
  const revoke = useRevokeInvitationMutation();
  const [revokeId, setRevokeId] = useState<string | null>(null);

  async function doResend(id: string, email: string): Promise<void> {
    try {
      await resend.mutateAsync(id);
      toast(`Invitation re-sent to ${email}.`);
    } catch {
      toast("Couldn't resend invitation.", "error");
    }
  }

  async function doRevoke(id: string): Promise<void> {
    try {
      await revoke.mutateAsync(id);
      toast("Invitation revoked.");
    } catch {
      toast("Couldn't revoke invitation.", "error");
    } finally {
      setRevokeId(null);
    }
  }

  return (
    <>
      <Box
        component="table"
        role="grid"
        aria-label="Pending invitations"
        sx={{
          width: "100%",
          borderCollapse: "collapse",
          "& th, & td": {
            textAlign: "left",
            py: 1.5,
            px: 1.5,
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
            fontSize: 14,
          },
          "& th": {
            color: "text.secondary",
            fontWeight: 500,
            fontSize: 12,
            textTransform: "uppercase",
            letterSpacing: 0.5,
          },
        }}
      >
        <thead>
          <tr role="row">
            <th role="columnheader" scope="col">
              Email
            </th>
            <th role="columnheader" scope="col">
              Role
            </th>
            <th role="columnheader" scope="col">
              Invited
            </th>
            <th role="columnheader" scope="col">
              Expires
            </th>
            <th role="columnheader" scope="col">
              By
            </th>
            <th role="columnheader" scope="col">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((inv) => {
            const rowName = `${inv.email} ${roleLabel(inv.role)}`;
            return (
              <tr
                key={inv.id}
                role="row"
                aria-label={rowName}
                data-invitation-id={inv.id}
              >
                <td>{inv.email}</td>
                <td>
                  <RoleChip role={inv.role} />
                </td>
                <td>
                  <span title={inv.invitedAt}>
                    {relativeTime(inv.invitedAt)}
                  </span>
                </td>
                <td>
                  <span title={inv.expiresAt}>
                    {relativeTime(inv.expiresAt)}
                  </span>
                </td>
                <td>{inv.invitedBy.displayName}</td>
                <td>
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        void doResend(inv.id, inv.email);
                      }}
                    >
                      Resend
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        setRevokeId(inv.id);
                      }}
                    >
                      Revoke
                    </Button>
                  </Stack>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Box>
      <ConfirmDialog
        open={revokeId !== null}
        title="Revoke invitation?"
        description="The recipient won't be able to use this invitation link anymore. You can always invite them again with the same email."
        confirmLabel="Revoke invitation"
        danger
        pending={revoke.isPending}
        onConfirm={() => {
          if (revokeId) void doRevoke(revokeId);
        }}
        onCancel={() => {
          setRevokeId(null);
        }}
      />
    </>
  );
}
